/**
 * Maps backend booking error messages to i18n translation keys.
 * The backend returns English error strings — this utility pattern-matches
 * them and returns the appropriate translated message via `t()`.
 */

const STATIC_ERROR_MAP = {
  'Missing required booking fields.': 'booking_errors.missing_fields',
  'Event not found': 'booking_errors.event_not_found',
  'Bookings are not available for this event.': 'booking_errors.bookings_not_available',
  'Event is not accepting bookings.': 'booking_errors.not_accepting_bookings',
  'Event has finished; bookings are closed.': 'booking_errors.event_finished',
  'Brand not found': 'booking_errors.brand_not_found',
  'Bookings are not available for this brand.': 'booking_errors.brand_not_available',
  'You already have an active booking for this event.': 'booking_errors.already_booked',
  'This space is fully booked.': 'booking_errors.space_fully_booked',
  // Promoter-side gate: approveBooking returns this when Spaces.quantity is
  // already 0 (another booking on the same space was approved/paid first).
  // Distinct from the brand-side "fully booked" message so the i18n string
  // can mention rejecting or expiring another booking to free a slot.
  'This space is fully booked. Approval would oversell. Cancel or expire another booking first.':
    'booking_errors.approval_no_availability',
  'That specific place is already booked.': 'booking_errors.place_already_booked',
  'No free placements left for this space.': 'booking_errors.no_placements_left',
  'That specific place was just taken. Please pick another.': 'booking_errors.place_just_taken',
  'Internal server error, unable to create booking.': 'booking_errors.internal_error',
  'Internal server error.': 'booking_errors.internal_error',
  'Internal server error': 'booking_errors.internal_error',
  'File too large. Maximum size is 25MB per file.': 'booking_errors.file_too_large',
  'Too many files. Maximum is 20 files.': 'booking_errors.too_many_files',
  'Invalid document type. Allowed: PDF, JPEG, PNG, WEBP, DOC, DOCX': 'booking_errors.invalid_document_type',
  'Invalid documentNames JSON.': 'booking_errors.invalid_upload',
  'documentNames must be an array.': 'booking_errors.invalid_upload',
  'documentNames array must match the number of uploaded files.': 'booking_errors.document_mismatch',
  'Invalid placementId for this event.': 'booking_errors.invalid_placement',
  'Invalid spaceId (must be an integer Space.id).': 'booking_errors.invalid_space',
  'Space not found': 'booking_errors.space_not_found',
  'The selected space does not belong to this event. Please refresh the page and choose a space again.':
    'booking_errors.space_event_mismatch',
  'Failed to create booking': 'booking_errors.generic',
  'Error approving booking': 'booking_errors.approve_failed',
  'Error rejecting booking': 'booking_errors.reject_failed',
};

/**
 * Dynamic patterns: [regex, translationKey, extractGroup]
 * extractGroup is the capture group index used as the `details` interpolation variable.
 */
const DYNAMIC_PATTERNS = [
  [/^Missing required documents:\s*(.+)$/i, 'booking_errors.missing_documents', 1],
  [/^This event requires the following documents:\s*(.+)$/i, 'booking_errors.event_requires_documents', 1],
  [/^Booking already (.+)\.$/i, 'booking_errors.booking_already_actioned', 1],
  [/^Upload error:\s*(.+)$/i, 'booking_errors.upload_error', 1],
  [/^File too large/i, 'booking_errors.file_too_large', null],
  [/FOR UPDATE cannot be applied/i, 'booking_errors.action_failed', null],
];

export function mapBookingError(rawMessage, t) {
  if (!rawMessage) return t('booking_errors.generic');

  const msg = typeof rawMessage === 'string' ? rawMessage.trim() : String(rawMessage);

  // Try static match first
  const staticKey = STATIC_ERROR_MAP[msg];
  if (staticKey) return t(staticKey);

  // Try dynamic patterns
  for (const [regex, key, groupIdx] of DYNAMIC_PATTERNS) {
    const match = msg.match(regex);
    if (match) {
      const details = groupIdx !== null ? match[groupIdx] : '';
      return t(key, { details });
    }
  }

  // Fallback: return raw message
  return msg;
}
